/**
 * FocusAreaCard
 *
 * One focus area: what it is, what you're practising, and how to act on it.
 *
 * A single column with one clear hierarchy, after the previous version stacked
 * four blocks of roughly equal weight:
 *
 * - The title shared its row with a right-aligned check-in label, which
 *   squeezed a short heading into three wrapped lines on a phone.
 * - The "Overdue" pill sat inside the heading flow and broke the line.
 * - The auto-retire prompt was a full amber alert box, making the least
 *   important thing on the card the loudest.
 * - Four equal-weight actions sat in a row, and the fourth was clipped off the
 *   right edge on a narrow screen.
 *
 * Now: title on its own line, both status signals on one quiet meta line, the
 * practice as the visual anchor (it is the thing you actually do), a single
 * primary action, and everything else behind an overflow menu.
 *
 * Retired cards show retirement metadata instead of actions.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pencil,
  Archive,
  RefreshCcw,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import type { FocusArea } from "@/types/focusArea";
import {
  useConfirmAutoRetire,
  useRegeneratePractice,
  useSnoozeAutoRetire,
} from "@/hooks/useFocusAreaMutations";
import { useStartCheckIn } from "@/hooks/useFocusAreaCheckIn";

interface FocusAreaCardProps {
  focusArea: FocusArea;
  onEdit: (fa: FocusArea) => void;
  onRetire: (fa: FocusArea) => void;
}

function lastCheckinLabel(isoString: string | null): string {
  if (!isoString) return "No check-ins yet";
  try {
    return `Checked in ${formatDistanceToNow(parseISO(isoString), {
      addSuffix: true,
    })}`;
  } catch {
    return "No check-ins yet";
  }
}

export function FocusAreaCard({
  focusArea,
  onEdit,
  onRetire,
}: FocusAreaCardProps) {
  const navigate = useNavigate();
  const isRetired = focusArea.status === "retired";
  const showRegenerate =
    focusArea.status === "active" && focusArea.current_practice !== null;

  const regenerateMutation = useRegeneratePractice();
  const startCheckIn = useStartCheckIn();
  const confirmMutation = useConfirmAutoRetire();
  const snoozeMutation = useSnoozeAutoRetire();
  const [editFirstHint, setEditFirstHint] = useState(false);

  const onRetireNow = () => {
    confirmMutation.mutate(focusArea.id, {
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 400) {
          toast.info(
            "This focus area isn't flagged for retirement anymore — we've refreshed your list."
          );
        }
      },
    });
  };

  const onNotYet = () => {
    snoozeMutation.mutate(focusArea.id);
  };

  const handleCheckIn = async () => {
    const response = await startCheckIn.mutateAsync(focusArea.id);
    navigate(`/growth/check-ins/${response.check_in.id}`);
  };

  const handleRegenerate = async () => {
    setEditFirstHint(false);
    try {
      await regenerateMutation.mutateAsync(focusArea.id);
    } catch (e) {
      const code = (
        e as {
          response?: { data?: { detail?: { code?: string } } };
        }
      )?.response?.data?.detail?.code;
      if (code === "regenerate_requires_edit") {
        setEditFirstHint(true);
      }
    }
  };

  return (
    <Card
      className="flex flex-col gap-3 p-4"
      data-testid={`focus-area-card-${focusArea.id}`}
    >
      {/* Title takes the full width. It previously shared the row with a
          right-aligned check-in label, which wrapped short headings across
          three lines on a phone. */}
      <div>
        <h3 className="text-base font-semibold leading-snug text-foreground">
          {focusArea.name}
        </h3>

        {/* Both status signals on one quiet line, out of the heading flow. */}
        {!isRetired && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {focusArea.is_overdue && (
              <>
                <span className="font-medium text-amber-700 dark:text-amber-400">
                  Overdue
                </span>
                <span aria-hidden="true">·</span>
              </>
            )}
            <span>{lastCheckinLabel(focusArea.last_checkin_at)}</span>
          </div>
        )}

        {/* Clamped: the description is context, not the point of the card.
            The full text stays reachable via the title attribute. */}
        <p
          className="mt-2 line-clamp-2 text-sm text-muted-foreground"
          title={focusArea.description}
        >
          {focusArea.description}
        </p>
      </div>

      {/* The practice is the thing you actually do, so it anchors the card. */}
      {focusArea.current_practice && (
        <div className="rounded-md border-l-2 border-primary/40 bg-accent/40 px-3 py-2 text-sm text-foreground">
          {focusArea.current_practice.text}
        </div>
      )}

      {/* Demoted from a full alert box. This is housekeeping; it should not
          outshout the focus area itself. */}
      {!isRetired && focusArea.auto_retire_candidate && (
        <div
          role="region"
          aria-label="Retirement prompt"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
        >
          <span>Looks done — {focusArea.auto_retire_candidate.reason}.</span>
          <button
            type="button"
            onClick={onRetireNow}
            disabled={confirmMutation.isPending}
            className="font-medium text-foreground underline underline-offset-4 disabled:opacity-50"
          >
            Retire now
          </button>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={onNotYet}
            disabled={snoozeMutation.isPending}
            className="underline underline-offset-4 disabled:opacity-50"
          >
            Not yet
          </button>
        </div>
      )}

      {isRetired ? (
        <div className="text-xs text-muted-foreground">
          Retired
          {focusArea.retired_at
            ? ` ${formatDistanceToNow(parseISO(focusArea.retired_at), {
                addSuffix: true,
              })}`
            : ""}
          {focusArea.retired_reason ? ` — ${focusArea.retired_reason}` : ""}
        </div>
      ) : (
        <>
          {/* One primary action; the rest move into a menu so nothing is
              clipped off the right edge on a narrow screen. */}
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => void handleCheckIn()}
              disabled={startCheckIn.isPending}
              className="gap-1.5"
              data-testid="check-in-button"
            >
              {startCheckIn.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Check in
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2"
                  aria-label="More actions"
                  data-testid={`focus-area-menu-${focusArea.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(focusArea)}>
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onRetire(focusArea)}>
                  <Archive className="mr-2 h-3.5 w-3.5" />
                  Retire
                </DropdownMenuItem>
                {showRegenerate && (
                  <DropdownMenuItem
                    onClick={() => void handleRegenerate()}
                    disabled={regenerateMutation.isPending}
                  >
                    <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                    New practice
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {editFirstHint && (
            <div className="text-xs text-muted-foreground" role="status">
              Edit the practice first — you&apos;ve used your regeneration.
            </div>
          )}
        </>
      )}
    </Card>
  );
}
